<template>
  <v-card id="account-rate-sheet-definition-line-form">
    <v-card-title :class="headerClass">
      <v-row>
        <v-col>
          <span :style="{ wordBreak: $vuetify.breakpoint.xs ? 'break-word' : 'normal' }">
            {{ title }}
          </span>
        </v-col>
        <v-col cols="auto">
          <BaseDialogActions elementId="actions" hideRefresh />
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pt-4" style="justify-items: center;">
      <v-row dense style="width: max(min(100%, 452px), 200px)">
        <v-col cols="12">
          <LoadableComponent :loading="speciesGroupsLoading || !canRenderLines">
            <v-autocomplete
              v-model="speciesGroup"
              :label="$t('speciesGroup')"
              :items="accountSpeciesGroups"
              :disabled="disableAutocompleteForList(accountSpeciesGroups)"
              @change="speciesGroupChanged"
              item-text="label"
              return-object
              clearable
              :prepend-icon="IconHelpers.getIconForObjectType('speciesGroup')"
              id="speciesGroup">
            </v-autocomplete>
          </LoadableComponent>
        </v-col>

        <v-col cols="12">
          <LoadableComponent :loading="lengthGroupsLoading || !canRenderLines">
            <LengthGroupAutocomplete
              :initSelection="lengthGroup"
              elementId="lengthGroup"
              :lengthGroups="lengthGroupOptions"
              @length-group-chosen="lengthGroupChosen">
            </LengthGroupAutocomplete>
          </LoadableComponent>
        </v-col>

        <v-col cols="12">
          <LoadableComponent :loading="diameterGroupsLoading || !canRenderLines">
            <DiameterGroupAutocomplete
              :initSelection="diameterGroup"
              elementId="diameterGroup"
              :diameterGroups="diameterGroupOptions"
              @diameter-group-chosen="diameterGroup = $event">
            </DiameterGroupAutocomplete>
          </LoadableComponent>
        </v-col>

        <v-col cols="12">
          <LoadableComponent :loading="!canRenderLines">
            <v-autocomplete
              v-model="grade"
              :label="$t('grade')"
              :items="gradesForSelection/*product.productGrades*/"
              :disabled="disableAutocompleteForList(gradesForSelection)"
              item-text="name"
              return-object
              clearable
              :prepend-icon="IconHelpers.getIconForObjectType('grade')"
              id="grade">
            </v-autocomplete>
          </LoadableComponent>
        </v-col>

        <v-col cols="12">
          <LoadableComponent :loading="!canRenderLines">
            <v-autocomplete
              v-model="endUseSort"
              :label="$t('endUseSort')"
              :items="endUseSortsForSelection"
              :disabled="disableAutocompleteForList(endUseSortsForSelection)"
              item-text="name"
              return-object
              clearable
              :prepend-icon="IconHelpers.getIconForObjectType('endUseSort')"
              id="endUseSort">
            </v-autocomplete>
          </LoadableComponent>
        </v-col>

        <v-col cols="12" class="pl-9" v-if="rateRenderTrigger">
          <MoneyTextField
          :initialValue="rate"
          :label="$t('rate')"
          :focus="focusRate"
          @val-changed="rateEntered"
          @enter="onEnterPressedOnRate()"
          elementId="rate">
          </MoneyTextField>
        </v-col>
      </v-row>
      <slot name="context" :line="rateSheetDefinitionLineBuilder"/>
    </v-card-text>
    <v-card-actions class="py-4 mx-0">
      <v-row dense align="center">
        <v-col>
          <v-btn color="primary" outlined @click="handleCancel" tabindex="-1">
            <span>
              {{ this.$t('cancel') }}
            </span>
          </v-btn>
        </v-col>

        <v-col cols="auto" v-if="!isValid">
          <Icon
          :tooltipText="invalidTooltip"
          :small="false"
          iconColor="error"
          icon="mdi-alert"/>
        </v-col>

        <v-col cols="auto" v-if="showSaveWithoutClosingButton">
          <v-btn color="primary" :disabled="!isValid" @click="handleSave">
            <span>
              {{ this.$t('save') }}
            </span>
          </v-btn>
        </v-col>

        <v-col cols="auto">
          <v-btn color="primary" :disabled="!isValid" @click="handleSaveAndClose">
            <span>
              {{ this.$t('saveAndClose') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>
<script>
import fieldRules from '@/utils/rules'
import { mapGetters, mapActions } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import { getSpeciesGroupByLabel } from '@/components/account/account-scaling/species-groups/SpeciesGroups'
import IconHelpers from '@/utils/IconHelpers'

const Mode = {
  Editing: 'editing',
  Creating: 'creating'
}
const Fields = {
  SpeciesGroup: 'speciesGroup',
  LengthGroup: 'lengthGroup',
  DiameterGroup: 'diameterGroup',
  Grade: 'grade',
  EndUseSort: 'endUseSort'
}

export default {
  name: 'AccountRateSheetDefinitionLineForm',

  props: {
    rateSheetDefinitionLine: {
      type: Object,
      required: false,
      default: () => ({
        lengthGroup: null,
        diameterGroup: null,
        speciesGroup: null,
        grade: null,
        endUseSort: null,
        rate: 0,
        localUuid: null
      })
    },
    account: {
      type: Object,
      required: true
    },
    rateSheetDefinition: {
      type: Object,
      required: true
    },
    selectedVersion: {
      type: Object,
      required: true
    },
    propSpeciesGroup: {
      type: Object,
      required: false,
      default: undefined
    },
    isRateSheetLevel: {
      type: Boolean,
      required: false,
      default: false
    },
    disableAutoSelect: {
      type: Boolean,
      required: false,
      default: false
    },
    headerClass: {
      type: String,
      required: false,
      default: 'primary white--text'
    }
  },

  components: {
    MoneyTextField: () => import('@/components/helper/MoneyTextField.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    LengthGroupAutocomplete: () => import('@/components/account/account-scaling/length-groups/LengthGroupAutocomplete.vue'),
    DiameterGroupAutocomplete: () => import('@/components/account/account-scaling/diameter-groups/DiameterGroupAutocomplete.vue')
  },

  data: () => ({
    IconHelpers,
    Mode,
    initialized: false,
    canRenderLines: false,
    rules: fieldRules.rules,
    rateSheetDefinitionLineBuilder: {
      rate: 0,
      lengthGroup: null,
      diameterGroup: null,
      speciesGroup: null,
      grade: null,
      endUseSort: null
    },
    fieldFocused: false,
    rateRenderTrigger: false,
    focusRate: false,
    isNotDuplicate: true,
    lengthGroupsLoading: false,
    diameterGroupsLoading: false,
    speciesGroupsLoading: false
  }),

  computed: {
    ...mapGetters('account-scaling', ['accountLengthGroups', 'accountIdOfLengthGroups', 'accountDiameterGroups', 'accountIdOfDiameterGroups', 'accountSpeciesGroups']),
    ...mapGetters('grade', ['allGrades']),
    ...mapGetters('end-use-sort', ['allEndUseSorts']),

    diameterGroupItems () {
      if (this.isEmpty(this.diameterGroups)) {
        return this.accountDiameterGroups
      }
      return this.diameterGroups
    },

    speciesGroupItems () {
      let result = this.speciesGroups
      if (this.isEmpty(result)) {
        result = this.accountSpeciesGroups
      }
      return result
    },

    lengthGroup: {
      get () { return this.rateSheetDefinitionLineBuilder.lengthGroup },
      set (lengthGroup) {
        this.setScalingField('lengthGroup', 'lengthGroupId', lengthGroup)
      }
    },

    diameterGroup: {
      get () { return this.rateSheetDefinitionLineBuilder.diameterGroup },
      set (diameterGroup) {
        this.setScalingField('diameterGroup', 'diameterGroupId', diameterGroup)
      }
    },

    speciesGroup: {
      get () { return this.rateSheetDefinitionLineBuilder.speciesGroup },
      set (speciesGroup) {
        this.setScalingField('speciesGroup', 'speciesGroupId', speciesGroup)
      }
    },

    grade: {
      get () { return this.rateSheetDefinitionLineBuilder.grade },
      set (grade) {
        this.setScalingField('grade', 'gradeId', grade)
      }
    },

    endUseSort: {
      get () { return this.rateSheetDefinitionLineBuilder.endUseSort },
      set (endUseSort) {
        this.setScalingField('endUseSort', 'endUseSortId', endUseSort)
      }
    },

    rate: {
      get () { return this.rateSheetDefinitionLineBuilder.rate },
      set (rate) {
        const asFloat = parseFloat(rate)
        if (isNaN(asFloat)) {
          this.rateSheetDefinitionLineBuilder.rate = 0
        } else {
          this.rateSheetDefinitionLineBuilder.rate = asFloat
        }
      }
    },

    title () {
      if (this.isRateSheetLevel) {
        return this.$t(this.mode === Mode.Editing ? 'editRateSheetLine' : 'createRateSheetLine')
      }
      return this.$t(this.mode === Mode.Editing ? 'editingRateSheetDefinitionFor' : 'creatingRateSheetDefinitionFor', { name: this.account.name })
    },

    accountId () {
      return this.account.accountId
    },

    isValid () {
      return this.atLeastOneAttribute &&
      this.isNotDuplicate
    },

    invalidTooltip () {
      if (this.isValid) {
        return undefined
      } else {
        if (!this.atLeastOneAttribute) {
          return this.$t('mustHaveAtLeastOneAttribute')
        } else {
          return this.$t('cannotDuplicate')
        }
      }
    },

    atLeastOneAttribute () {
      const { speciesGroup, lengthGroup, diameterGroup, grade, endUseSort } = this.rateSheetDefinitionLineBuilder
      return speciesGroup || lengthGroup || diameterGroup || grade || endUseSort
    },

    mode () {
      if (this.isRateSheetLevel) {
        return (this.rateSheetDefinitionLine?.uuid || this.rateSheetDefinitionLine?.rateSheetLineId)
          ? Mode.Editing : Mode.Creating
      }
      return this.rateSheetDefinitionLine ? Mode.Editing : Mode.Creating
    },

    lengthGroupOptions () {
      let unfiltered
      if (this.speciesGroup?.lengthGroups?.length > 0) {
        unfiltered = this.speciesGroup.lengthGroups
      } else {
        unfiltered = this.accountLengthGroups
      }
      return unfiltered.sort((a, b) => a.label.localeCompare(b.label))
    },

    diameterGroupOptions () {
      if (this.speciesGroup?.diameterGroups?.length > 0) {
        return this.speciesGroup.diameterGroups
      } else {
        return this.accountDiameterGroups
      }
    },

    linesKey () {
      return this.isRateSheetLevel ? 'rateSheetLines' : 'rateSheetDefinitionLines'
    },

    showSaveWithoutClosingButton () {
      return this.mode === Mode.Creating
    },

    gradesForSelection () {
      return this.allGrades
    },

    endUseSortsForSelection () {
      return this.allEndUseSorts
    }
  },

  async mounted () {
    await this.initialize()
    document.addEventListener('keydown', this.onKeydown)
    document.addEventListener('keyup', this.onKeyup)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('keyup', this.onKeyup)
  },

  watch: {
    rateSheetDefinitionLineBuilder: {
      handler () { this.detectDuplicate() },
      deep: true
    },

    'selectedVersion.rateSheetDefinitionLines': {
      handler () { this.detectDuplicate(); this.smartFocus() },
      deep: true
    },

    'selectedVersion.rateSheetLines': {
      handler () { this.detectDuplicate() },
      deep: true
    }
  },

  methods: {
    getSpeciesGroupByLabel,

    ...mapActions('account-scaling', ['fetchAccountLengthGroups', 'fetchAccountDiameterGroups', 'fetchAccountSpeciesGroups']),
    ...mapActions('grade', ['fetchGrades']),
    ...mapActions('end-use-sort', ['fetchEndUseSorts']),

    detectDuplicate () {
      const lines = this.selectedVersion[this.linesKey]
      this.isNotDuplicate = lines
        .every(l => {
          return (
            this.isSameLine(l) ||
            this.uniqueLengthGroup(l.lengthGroup) ||
            this.uniqueDiameterGroup(l.diameterGroup) ||
            this.uniqueSpeciesGroup(l.speciesGroup) ||
            this.uniqueGrade(l.grade) ||
            this.uniqueEndUseSort(l.endUseSort)
          )
        })
    },
    isSameLine (l) {
      if (!!l.rateSheetLineId || !!this.rateSheetDefinitionLineBuilder?.rateSheetLineId) {
        return l.rateSheetLineId === this.rateSheetDefinitionLineBuilder?.rateSheetLineId
      }
      if (!!l.rateSheetDefinitionLineId || !!this.rateSheetDefinitionLineBuilder?.rateSheetDefinitionLineId) {
        return l.rateSheetDefinitionLineId === this.rateSheetDefinitionLineBuilder?.rateSheetDefinitionLineId
      }
      if (this.rateSheetDefinitionLineBuilder?.uuid && l.uuid) {
        return l.uuid === this.rateSheetDefinitionLineBuilder.uuid
      }
      return false
    },
    uniqueLengthGroup (lg) { return this.lengthGroup?.lengthGroupId !== lg?.lengthGroupId && (lg?.lengthGroupId != null || this.lengthGroup?.lengthGroupId != null) },
    uniqueDiameterGroup (dg) { return this.diameterGroup?.diameterGroupId !== dg?.diameterGroupId && (dg?.diameterGroupId != null || this.diameterGroup?.diameterGroupId != null) },
    uniqueSpeciesGroup (sg) { return this.speciesGroup?.speciesGroupId !== sg?.speciesGroupId && (sg?.speciesGroupId != null || this.speciesGroup?.speciesGroupId != null) },
    uniqueGrade (g) { return this.grade?.gradeId !== g?.gradeId && (g?.gradeId != null || this.grade?.gradeId != null) },
    uniqueEndUseSort (eus) { return this.endUseSort?.endUseSortId !== eus?.endUseSortId && (eus?.endUseSortId != null || this.endUseSort?.endUseSortId != null) },

    async initialize () {
      if (this.rateSheetDefinitionLine) {
        this.rateSheetDefinitionLineBuilder = cloneDeep(this.rateSheetDefinitionLine)
      } else {
        this.rateSheetDefinitionLineBuilder = this.getBlankLine()
        if (this.propSpeciesGroup) {
          this.speciesGroup = this.propSpeciesGroup
          this.speciesGroupChanged(this.propSpeciesGroup)
        }
      }
      if (this.rateSheetDefinitionLineBuilder?.rate > 0) {
        this.rateEntered(this.rateSheetDefinitionLineBuilder.rate)
      }

      this.rateRenderTrigger = true
      await this.fetchOptionData()
      this.canRenderLines = true
      this.setSelectionsAndFocus()
      this.initialized = true
    },

    async fetchOptionData () {
      await this.fetchAndAssignSpeciesGroups()
      await this.fetchAndAssignLengthGroups()
      await this.fetchAndAssignDiameterGroups()
      await this.fetchAndAssignGrades()
      await this.fetchAndAssignEndUseSorts()
    },

    async fetchAndAssignSpeciesGroups () {
      this.speciesGroupsLoading = true
      if (this.accountSpeciesGroups.length === 0 || !this.accountSpeciesGroups.every(sg => sg.accountId === this.accountId)) {
        await this.fetchAccountSpeciesGroups(this.accountId)
      }
      this.assignBuilderSpeciesGroup()
      this.speciesGroupsLoading = false
    },

    assignBuilderSpeciesGroup () {
      this.speciesGroup = this.accountSpeciesGroups.find(sg => sg.speciesGroupId === this.rateSheetDefinitionLineBuilder.speciesGroup?.speciesGroupId) ||
        this.rateSheetDefinitionLineBuilder.speciesGroup ||
          null
    },

    async fetchAndAssignLengthGroups () {
      this.lengthGroupsLoading = true
      if (this.accountIdOfLengthGroups !== this.accountId) {
        await this.fetchAccountLengthGroups(this.accountId)
      }
      this.assignBuilderLengthGroup()
      this.lengthGroupsLoading = false
    },

    assignBuilderLengthGroup () {
      this.lengthGroup = this.accountLengthGroups.find(lg => lg.lengthGroupId === this.rateSheetDefinitionLineBuilder.lengthGroup?.lengthGroupId) ||
        this.rateSheetDefinitionLineBuilder.lengthGroup ||
          null
    },

    async fetchAndAssignDiameterGroups () {
      this.diameterGroupsLoading = true
      if (this.accountIdOfDiameterGroups !== this.accountId) {
        await this.fetchAccountDiameterGroups(this.accountId)
      }
      this.assignBuilderDiameterGroup()
      this.diameterGroupsLoading = false
    },

    assignBuilderDiameterGroup () {
      this.diameterGroup = this.accountDiameterGroups.find(dg => dg.diameterGroupId === this.rateSheetDefinitionLineBuilder.diameterGroup?.diameterGroupId) ||
        this.rateSheetDefinitionLineBuilder.diameterGroup ||
          null
    },

    async fetchAndAssignGrades () {
      if (this.allGrades.length === 0) {
        await this.fetchGrades()
      }
      this.assignBuilderGrade()
    },

    assignBuilderGrade () {
      this.grade = this.allGrades.find(g => g.gradeId === this.rateSheetDefinitionLineBuilder.grade?.gradeId) ||
        this.rateSheetDefinitionLineBuilder.grade ||
          null
    },

    async fetchAndAssignEndUseSorts () {
      if (this.allEndUseSorts.length === 0) {
        await this.fetchEndUseSorts()
      }
      this.assignBuilderEndUseSort()
    },

    assignBuilderEndUseSort () {
      this.endUseSort = this.allEndUseSorts.find(eus => eus.endUseSortId === this.rateSheetDefinitionLineBuilder.endUseSort?.endUseSortId) ||
        this.rateSheetDefinitionLineBuilder.endUseSort ||
          null
    },

    setSelectionsAndFocus () {
      switch (this.mode) {
        case Mode.Creating: {
          const fields = {
            speciesGroup: this.speciesGroupItems,
            lengthGroup: this.lengthGroupItems,
            diameterGroup: this.diameterGroupItems,
            grade: this.gradesForSelection,
            endUseSort: this.endUseSortsForSelection
          }
          Object.entries(fields).forEach(([fieldName, elements]) => {
            if (elements) this.setToOnlyElementOrFocusField(elements, fieldName)
          })
          break
        }
        case Mode.Editing: {
          break
        }
      }

      if (!this.fieldFocused) {
        this.focusRate = true
      }
    },

    setToOnlyElementOrFocusField (elements, fieldName) {
      if (elements.length === 1 && fieldName !== 'endUseSort' && !this.disableAutoSelect) {
        this[fieldName] = elements[0]
      } else if (!this.fieldFocused) {
        this.focus(fieldName)
        this.fieldFocused = true
      }
    },

    focus (elementId) {
      this.$nextTick(_ => {
        const e = document.getElementById(elementId)
        e.focus()
      })
    },

    isEmpty (array) {
      return !(array?.length > 0)
    },

    rateEntered (rate) {
      this.rate = rate
    },

    setScalingField (fieldName, idFieldName, value) {
      this.$set(this.rateSheetDefinitionLineBuilder, fieldName, value)
      this.$set(this.rateSheetDefinitionLineBuilder, idFieldName, value?.[idFieldName] ?? null)
    },

    handleSaveAndClose () {
      const isValid = this.isValid
      if (isValid) {
        this.$emit('save-and-close', this.rateSheetDefinitionLineBuilder)
      } else {
        if (!this.atLeastOneAttribute) {
          this.setSnackError(this.$t('mustHaveAtLeastOneAttribute'))
        } else {
          this.setSnackError(this.$t('rateSheetDefinitionLineNotValid'))
        }
      }
    },

    handleSave () {
      const isValid = this.isValid
      if (isValid) {
        this.$emit('save', this.rateSheetDefinitionLineBuilder)
        this.rate = 0
        this.triggerRateRerender()
      } else {
        if (!this.atLeastOneAttribute) {
          this.setSnackError(this.$t('mustHaveAtLeastOneAttribute'))
        } else {
          this.setSnackError(this.$t('rateSheetDefinitionLineNotValid'))
        }
      }
    },

    handleCancel () {
      this.$emit('cancel', this.mode)
    },

    getBlankLine () {
      return {
        lengthGroup: null,
        lengthGroupId: null,
        diameterGroup: null,
        diameterGroupId: null,
        speciesGroup: null,
        speciesGroupId: null,
        grade: null,
        gradeId: null,
        endUseSort: null,
        endUseSortId: null,
        rate: null
      }
    },

    onKeydown (e) {
      if (e.keyCode === 9) { // tab
        this.removeClearButtonsFromTabIndex()
      }
    },

    onKeyup (e) {
      if (!document.hasFocus() && e.key.toLowerCase() === 'enter') {
        this.mode === Mode.Editing ? this.handleSaveAndClose() : this.handleSave()
      }
    },

    onEnterPressedOnRate () {
      this.mode === Mode.Editing ? this.handleSaveAndClose() : this.handleSave()
    },

    triggerRateRerender () {
      this.rateRenderTrigger = false
      this.$nextTick(() => {
        this.rateRenderTrigger = true
      })
    },

    disableAutocompleteForList (list) {
      return false
    },

    speciesGroupChanged (speciesGroup) {
      if (speciesGroup?.lengthGroups?.length === 1) {
        this.lengthGroup = speciesGroup.lengthGroups[0]
      } else {
        if (this.lengthGroup) {
          if (!speciesGroup?.lengthGroups?.some(lg => lg.lengthGroupId === this.lengthGroup.lengthGroupId)) {
            this.lengthGroup = null
          }
        }
      }

      if (speciesGroup?.diameterGroups?.length === 1) {
        this.diameterGroup = speciesGroup.diameterGroups[0]
      } else {
        if (this.diameterGroup) {
          if (!speciesGroup?.diameterGroups?.some(dg => dg.diameterGroupId === this.diameterGroup.diameterGroupId)) {
            this.diameterGroup = null
          }
        }
      }
    },

    removeClearButtonsFromTabIndex () {
      const clearButtons = document.querySelectorAll('#account-rate-sheet-definition-line-form .v-input__icon--clear button')
      clearButtons.forEach(button => {
        button.setAttribute('tabindex', '-1')
      })
    },

    smartFocus () {
      const fields = Object.values(Fields)
      let focused = false
      fields.reverse().forEach(field => {
        if (!focused) {
          focused = this.smartFocusField(field, fields.filter(f => f !== field))
        }
      })
    },

    smartFocusField (field, otherFields) {
      const lines = this.selectedVersion[this.linesKey]
      const matchingLines = lines.filter(line => {
        return otherFields.every(f => this.linesMatchField(f, line))
      })
      let options
      switch (field) {
        case Fields.SpeciesGroup: options = this.accountSpeciesGroups; break
        case Fields.LengthGroup: options = this.lengthGroupOptions; break
        case Fields.DiameterGroup: options = this.diameterGroupOptions; break
        case Fields.Grade: options = this.gradesForSelection; break
        case Fields.EndUseSort: options = this.endUseSortsForSelection; break
      }
      if (!options) return false
      const focus = options.some(o => matchingLines.every(l => l[`${field}Id`] !== o[`${field}Id`]))
      if (focus) {
        this.focus(field)
      }
      return focus
    },

    linesMatchField (fieldName, line) {
      const id = `${fieldName}Id`
      const lineFromBuilder = this.rateSheetDefinitionLineBuilder

      const idFromLine = line[id]
      const idFromBuilder = lineFromBuilder[fieldName] ? lineFromBuilder[fieldName][id] : null

      return (idFromBuilder == null && idFromLine == null) || idFromBuilder === idFromLine
    },

    lengthGroupChosen (lengthGroup) {
      this.lengthGroup = lengthGroup
    }
  }
}
</script>
